import { useState, useEffect, useCallback } from 'react'
import { CLOUD_API, CLOUD_NOT_CONNECTED } from './shared'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, th } from '@ui/classes'
import { badgeIncoming, badgeRepaid, credInput, errBanner, okBanner } from '@ui/admin'

import { httpErrorText } from '@core/utils/httpError'
interface CloudUser {
  id: string
  login: string
  role: 'admin' | 'cashier'
  is_active: boolean
  permissions: {
    can_create_on_pos: boolean
    can_manage_shifts: boolean
    can_correction?: boolean
    can_refund?: boolean
    can_edit_catalog?: boolean
    can_skip_fiscalization?: boolean
    can_skip_marking?: boolean
    can_writeoff?: boolean
    can_approve_marking_hold?: boolean
  }
  /** Встроенный пресет роли (ARCH-TASK-64); null — «свои права». */
  role_preset: string | null
  has_pin: boolean
}

/** Встроенный пресет прав (ARCH-TASK-64). */
interface RolePreset {
  key: string
  name: string
  description: string
  permissions: CloudUser['permissions']
}

/**
 * Дополнительные права 65.10: чеки без ККТ, пропуск маркировки,
 * списания и одобрение чеков на проверке. Вынесены в конфиг, чтобы таблица и форма
 * «Свои права» не расходились.
 */
const EXTRA_PERMISSIONS = [
  {
    key: 'can_skip_fiscalization',
    short: 'Без ККТ',
    title: 'Снять фискализацию (немаркированный товар)'
  },
  {
    key: 'can_skip_marking',
    short: 'Без кода',
    title: 'Продать маркированный товар без кода (только наличные)'
  },
  { key: 'can_writeoff', short: 'Списание', title: 'Оформлять списание товара' },
  {
    key: 'can_approve_marking_hold',
    short: 'Одобрение Честного Знака',
    title: 'Одобрять/отменять чеки на проверке маркировки'
  }
] as const

/** Человеческий текст ошибки роли (сервер отдаёт машинные коды). */
function roleError(code: string | undefined, status: number): string {
  if (code === 'unknown_role_preset') return 'Неизвестная роль — обновите список ролей'
  if (code === 'role_preset_only_for_cashiers') return 'Роль назначается только кассирам'
  return httpErrorText('Сотрудник', status)
}

/**
 * Секция управления кассирами и правами (RBAC).
 * Таблица сотрудников, регистрация кассира с PIN, редактирование прав,
 * смена PIN, блокировка/разблокировка.
 */

export function UsersSection({ token }: { token: string }) {
  const [users, setUsers] = useState<CloudUser[]>([])
  const [presets, setPresets] = useState<RolePreset[]>([])
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    login: '',
    pin: '',
    /** 'custom' — права задаются галочками, иначе ключ пресета. */
    rolePreset: 'cashier',
    can_create_on_pos: true,
    can_manage_shifts: true,
    // Чек коррекции — право, выдаваемое осознанно (по умолчанию выключено).
    can_correction: false,
    // Возвраты — по умолчанию выключено (только старший/управляющий, 65).
    can_refund: false,
    // Одобрение заявок с касс (по умолчанию выключено).
    can_edit_catalog: false,
    // Исключения из фискализации/маркировки (65.10) — только осознанно.
    can_skip_fiscalization: false,
    can_skip_marking: false,
    can_writeoff: false,
    can_approve_marking_hold: false
  })

  const reload = useCallback(async () => {
    // Без токена запрос гарантированно вернёт 401 — показываем понятное состояние.
    if (!token) {
      setUsers([])
      setPresets([])
      setErr(null)
      return
    }
    try {
      const [usersResp, presetsResp] = await Promise.all([
        fetch(`${CLOUD_API}/auth/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${CLOUD_API}/auth/role-presets`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])
      if (!usersResp.ok) {
        setErr(httpErrorText('Загрузка сотрудников', usersResp.status))
        return
      }
      setUsers((await usersResp.json()) as CloudUser[])
      if (presetsResp.ok) {
        const json = (await presetsResp.json()) as { items?: RolePreset[] }
        setPresets(json.items ?? [])
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [token])

  useEffect(() => {
    void reload()
  }, [reload])

  const createCashier = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    setNote(null)
    if (!/^\d{4}$/.test(form.pin)) {
      setErr('PIN должен состоять ровно из 4 цифр')
      return
    }
    setBusy(true)
    try {
      // Роль → сервер сам подставит права пресета; «свои» — явные флаги.
      const body =
        form.rolePreset === 'custom'
          ? {
              login: form.login,
              pin: form.pin,
              permissions: {
                can_create_on_pos: form.can_create_on_pos,
                can_manage_shifts: form.can_manage_shifts,
                can_correction: form.can_correction,
                can_refund: form.can_refund,
                can_edit_catalog: form.can_edit_catalog,
                can_skip_fiscalization: form.can_skip_fiscalization,
                can_skip_marking: form.can_skip_marking,
                can_writeoff: form.can_writeoff,
                can_approve_marking_hold: form.can_approve_marking_hold
              }
            }
          : { login: form.login, pin: form.pin, role_preset: form.rolePreset }

      const resp = await fetch(`${CLOUD_API}/auth/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      })
      if (!resp.ok) {
        const payload = (await resp.json().catch(() => ({}))) as { error?: string }
        setErr(
          payload.error
            ? roleError(payload.error, resp.status)
            : httpErrorText('Создание сотрудника', resp.status)
        )
        return
      }
      setNote(`Кассир «${form.login}» зарегистрирован`)
      setForm({
        login: '',
        pin: '',
        rolePreset: 'cashier',
        can_create_on_pos: true,
        can_manage_shifts: true,
        can_correction: false,
        can_refund: false,
        can_edit_catalog: false,
        can_skip_fiscalization: false,
        can_skip_marking: false,
        can_writeoff: false,
        can_approve_marking_hold: false
      })
      await reload()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const patchUser = async (id: string, patch: Record<string, unknown>) => {
    setErr(null)
    try {
      const resp = await fetch(`${CLOUD_API}/auth/users/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch)
      })
      if (!resp.ok) {
        const payload = (await resp.json().catch(() => ({}))) as { error?: string }
        setErr(
          payload.error
            ? roleError(payload.error, resp.status)
            : httpErrorText('Обновление сотрудника', resp.status)
        )
        return
      }
      await reload()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  const changePin = async (id: string) => {
    const pin = window.prompt('Новый 4-значный PIN:')
    if (pin == null) return
    if (!/^\d{4}$/.test(pin)) {
      setErr('PIN должен состоять ровно из 4 цифр')
      return
    }
    await patchUser(id, { pin })
    setNote('PIN обновлён')
  }

  return (
    <div>
      {note && <p className={okBanner}>{note}</p>}
      {err && <p className={errBanner}>{err}</p>}
      {!token && (
        <p className={hint}>
          {CLOUD_NOT_CONNECTED}. Управление сотрудниками и правами доступно после входа.
        </p>
      )}

      <div className={tableWrap}>
        <table className={table}>
          <thead>
            <tr>
              <th className={th}>Логин</th>
              <th className={th}>Роль</th>
              <th className={th}>Статус</th>
              <th className={th}>Товары на кассе</th>
              <th className={th}>Управление сменами</th>
              <th className={th}>Чек коррекции</th>
              <th className={th}>Возвраты</th>
              <th className={th}>Заявки с касс</th>
              {EXTRA_PERMISSIONS.map((p) => (
                <th key={p.key} className={th} title={p.title}>
                  {p.short}
                </th>
              ))}
              <th className={th}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className={td}>{u.login}</td>
                <td className={td}>
                  {u.role === 'admin' ? (
                    <span className={hint} title="Администратор имеет все права">
                      Администратор
                    </span>
                  ) : (
                    <select
                      value={u.role_preset ?? 'custom'}
                      onChange={(e) =>
                        void patchUser(u.id, {
                          role_preset: e.target.value === 'custom' ? null : e.target.value
                        })
                      }
                      className={credInput}
                      title="Роль задаёт набор прав одним выбором"
                    >
                      {/* Если пресеты не загрузились, показываем текущий ключ,
                          чтобы селект не «врал» первым пунктом. */}
                      {u.role_preset != null &&
                        !presets.some((preset) => preset.key === u.role_preset) && (
                          <option value={u.role_preset}>Роль «{u.role_preset}»</option>
                        )}
                      {presets.map((preset) => (
                        <option key={preset.key} value={preset.key}>
                          {preset.name}
                        </option>
                      ))}
                      <option value="custom">Свои права</option>
                    </select>
                  )}
                </td>
                <td className={td}>
                  <span className={u.is_active ? badgeRepaid : badgeIncoming}>
                    {u.is_active ? 'Активен' : 'Заблокирован'}
                  </span>
                </td>
                <td className={td}>
                  <input
                    type="checkbox"
                    checked={u.permissions?.can_create_on_pos ?? true}
                    disabled={u.role === 'admin'}
                    onChange={(e) =>
                      void patchUser(u.id, {
                        permissions: {
                          ...u.permissions,
                          can_create_on_pos: e.target.checked
                        }
                      })
                    }
                   className="h-4 w-4 cursor-pointer accent-ok"/>
                </td>
                <td className={td}>
                  <input
                    type="checkbox"
                    checked={u.permissions?.can_manage_shifts ?? true}
                    disabled={u.role === 'admin'}
                    onChange={(e) =>
                      void patchUser(u.id, {
                        permissions: {
                          ...u.permissions,
                          can_manage_shifts: e.target.checked
                        }
                      })
                    }
                   className="h-4 w-4 cursor-pointer accent-ok"/>
                </td>
                <td className={td}>
                  <input
                    type="checkbox"
                    checked={
                      u.permissions?.can_correction ??
                      (u.permissions?.can_manage_shifts !== false)
                    }
                    disabled={u.role === 'admin'}
                    onChange={(e) =>
                      void patchUser(u.id, {
                        permissions: {
                          ...u.permissions,
                          can_correction: e.target.checked
                        }
                      })
                    }
                   className="h-4 w-4 cursor-pointer accent-ok"/>
                </td>
                <td className={td}>
                  <input
                    type="checkbox"
                    checked={u.permissions?.can_refund === true}
                    disabled={u.role === 'admin'}
                    title="Оформление возвратов на кассе"
                    onChange={(e) =>
                      void patchUser(u.id, {
                        permissions: {
                          ...u.permissions,
                          can_refund: e.target.checked
                        }
                      })
                    }
                    className="h-4 w-4 cursor-pointer accent-ok"
                  />
                </td>
                <td className={td}>
                  {u.role === 'admin' ? (
                    <span
                      className={hint}
                      title="Роль admin имеет все права, включая одобрение заявок"
                    >
                      все права (admin)
                    </span>
                  ) : (
                    <input
                      type="checkbox"
                      checked={u.permissions?.can_edit_catalog === true}
                      onChange={(e) =>
                        void patchUser(u.id, {
                          permissions: {
                            ...u.permissions,
                            can_edit_catalog: e.target.checked
                          }
                        })
                      }
                      className="h-4 w-4 cursor-pointer accent-ok"
                    />
                  )}
                </td>
                {EXTRA_PERMISSIONS.map((p) => (
                  <td key={p.key} className={td}>
                    {u.role === 'admin' ? (
                      <span className={hint} title="У роли admin есть все права">
                        да
                      </span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={u.permissions?.[p.key] === true}
                        title={p.title}
                        onChange={(e) =>
                          void patchUser(u.id, {
                            permissions: {
                              ...u.permissions,
                              [p.key]: e.target.checked
                            }
                          })
                        }
                        className="h-4 w-4 cursor-pointer accent-ok"
                      />
                    )}
                  </td>
                ))}
                <td className={td}>
                  <button onClick={() => void changePin(u.id)} className={cx(btnBase, btn.secondary, btnSize.sm)}>Сменить PIN</button>{' '}
                  {u.role !== 'admin' && (
                    <button onClick={() => void patchUser(u.id, { is_active: !u.is_active })} className={cx(btnBase, btn.secondary, btnSize.sm)}>
                      {u.is_active ? 'Блокировать' : 'Разблокировать'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={hint}>
        Роль задаёт набор прав одним выбором; любую галочку можно поправить вручную — тогда роль
        станет «Свои права». Изменения применяются на кассе при следующей синхронизации (до 60
        секунд), повторный PIN-вход не требуется.
      </p>

      {presets.length > 0 && (
        <div className={hint}>
          {presets.map((preset) => (
            <div key={preset.key} className="py-0.5">
              <strong>{preset.name}</strong> — {preset.description}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={createCashier} className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-small">
          Имя (логин)
          <input value={form.login} onChange={(e) => setForm((f) => ({ ...f, login: e.target.value }))} className={credInput} />
        </label>
        <label className="flex flex-col gap-1 text-small">
          PIN (4 цифры)
          <input
            value={form.pin}
            onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value }))}
            className={credInput}
            inputMode="numeric"
            maxLength={4}
          />
        </label>
        <label className="flex flex-col gap-1 text-small">
          Роль
          <select
            value={form.rolePreset}
            onChange={(e) => setForm((f) => ({ ...f, rolePreset: e.target.value }))}
            className={credInput}
          >
            {presets.map((preset) => (
              <option key={preset.key} value={preset.key}>
                {preset.name}
              </option>
            ))}
            <option value="custom">Свои права</option>
          </select>
        </label>

        {form.rolePreset === 'custom' && (
          <>
            <label className="flex items-center gap-1.5 text-small">
              <input
                type="checkbox"
                checked={form.can_create_on_pos}
                onChange={(e) => setForm((f) => ({ ...f, can_create_on_pos: e.target.checked }))}
               className="h-4 w-4 cursor-pointer accent-ok"/>
              Товары на кассе
            </label>
            <label className="flex items-center gap-1.5 text-small">
              <input
                type="checkbox"
                checked={form.can_manage_shifts}
                onChange={(e) => setForm((f) => ({ ...f, can_manage_shifts: e.target.checked }))}
               className="h-4 w-4 cursor-pointer accent-ok"/>
              Управление сменами
            </label>
            <label className="flex items-center gap-1.5 text-small">
              <input
                type="checkbox"
                checked={form.can_correction}
                onChange={(e) => setForm((f) => ({ ...f, can_correction: e.target.checked }))}
               className="h-4 w-4 cursor-pointer accent-ok"/>
              Чек коррекции
            </label>
            <label className="flex items-center gap-1.5 text-small">
              <input
                type="checkbox"
                checked={form.can_refund}
                onChange={(e) => setForm((f) => ({ ...f, can_refund: e.target.checked }))}
               className="h-4 w-4 cursor-pointer accent-ok"/>
              Возвраты
            </label>
            <label className="flex items-center gap-1.5 text-small">
              <input
                type="checkbox"
                checked={form.can_edit_catalog}
                onChange={(e) => setForm((f) => ({ ...f, can_edit_catalog: e.target.checked }))}
                className="h-4 w-4 cursor-pointer accent-ok"
              />
              Заявки с касс
            </label>
            {EXTRA_PERMISSIONS.map((p) => (
              <label key={p.key} className="flex items-center gap-1.5 text-small" title={p.title}>
                <input
                  type="checkbox"
                  checked={form[p.key]}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, [p.key]: e.target.checked }))
                  }
                  className="h-4 w-4 cursor-pointer accent-ok"
                />
                {p.short}
              </label>
            ))}
          </>
        )}
        <button type="submit" disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
          + Зарегистрировать кассира
        </button>
      </form>
    </div>
  )
}
